import {
  createContext,
  Dispatch,
  SetStateAction,
  useState as useClassicState,
} from 'react';
import { createState, StateMethods, useState } from '@hookstate/core';

import { UpdateStatusType } from '@models/UpdateStatus.model';
import { IManufacturerCreateUpdate } from '@models/Manufacturer.model';
import { IProductCreateUpdate } from '@models/Product.model';
import { IUpdateRawQueryList } from '@models/Update.model';
import { CreateUpdatesDialogRaw } from './CreateUpdatesDialog.raw';
import { CreateUpdatePriority } from './components/Details/types';

export type RelatesToOptions = 'manufacturers' | 'products' | 'tags' | null;

export type ProductManufacturer = {
  value: string;
  label: string;
} | null;

export interface ICreateUpdateContext {
  relatesTo: StateMethods<RelatesToOptions>;
  selectedMenufacturers: IManufacturerCreateUpdate[];
  setSelectedMenufacturers: Dispatch<
    SetStateAction<IManufacturerCreateUpdate[]>
  >;
  selectedProducts: IProductCreateUpdate[];
  setSelectedProducts: Dispatch<SetStateAction<IProductCreateUpdate[]>>;
  searchManufacturer: StateMethods<string>;
  searchProducts: StateMethods<string>;
  detailsIsValid: StateMethods<boolean>;
  title: StateMethods<string>;
  priority: StateMethods<CreateUpdatePriority>;
  date: StateMethods<string | null>;
  description: StateMethods<string>;
  productManufacturerValue: ProductManufacturer;
  setProductManufacturerValue: Dispatch<SetStateAction<ProductManufacturer>>;
}

export const CreateUpdateContext = createContext<ICreateUpdateContext>({
  relatesTo: createState<RelatesToOptions>(null),
  selectedMenufacturers: [],
  setSelectedMenufacturers: () => {},
  selectedProducts: [],
  setSelectedProducts: () => {},
  searchManufacturer: createState<string>(''),
  searchProducts: createState<string>(''),
  detailsIsValid: createState<boolean>(false),
  title: createState<string>(''),
  priority: createState<CreateUpdatePriority>(UpdateStatusType.NEW),
  date: createState<string | null>(null),
  description: createState<string>(''),
  productManufacturerValue: null,
  setProductManufacturerValue: () => {},
});

interface ICreateUpdatesDialogQuery {
  open: boolean;
  onClose: () => void;
  edit?: {
    updatePrev: IUpdateRawQueryList;
  };
}

export const CreateUpdatesDialogQuery = (props: ICreateUpdatesDialogQuery) => {
  const relatesTo = useState<RelatesToOptions>(null);
  const [selectedMenufacturers, setSelectedMenufacturers] = useClassicState<
    IManufacturerCreateUpdate[]
  >([]);
  const [selectedProducts, setSelectedProducts] = useClassicState<
    IProductCreateUpdate[]
  >([]);
  const searchManufacturer = useState<string>('');
  const searchProducts = useState<string>('');
  const detailsIsValid = useState<boolean>(false);
  const title = useState<string>('');
  const priority = useState<CreateUpdatePriority>(UpdateStatusType.NEW);
  const date = useState<string | null>(null);
  const description = useState<string>('');
  const [productManufacturerValue, setProductManufacturerValue] =
    useClassicState<ProductManufacturer>(null);

  return (
    <CreateUpdateContext.Provider
      value={{
        relatesTo,
        selectedMenufacturers,
        setSelectedMenufacturers,
        selectedProducts,
        setSelectedProducts,
        searchManufacturer,
        searchProducts,
        detailsIsValid,
        title,
        priority,
        date,
        description,
        productManufacturerValue,
        setProductManufacturerValue,
      }}
    >
      <CreateUpdatesDialogRaw {...props} />
    </CreateUpdateContext.Provider>
  );
};
